import { BusinessIdea } from '../services/businessIdeaTypes';
import { BusinessPlanAdditionalData } from '../components/business-plan/BusinessPlanWizard';

// Define SectionId as a type for backward compatibility
export type SectionId = 
  | 'cover-page'
  | 'table-of-contents'
  | 'overview'
  | 'executive-summary'
  | 'swot-analysis'
  | 'business-models'
  | 'viability-analysis'
  | 'market-research'
  | 'products-services'
  | 'sales-marketing'
  | 'operations'
  | 'financials'
  | 'implementation-plan';

// Also export as an enum for use with Object.values
export enum SectionIdEnum {
  COVER_PAGE = 'cover-page',
  TABLE_OF_CONTENTS = 'table-of-contents',
  OVERVIEW = 'overview',
  EXECUTIVE_SUMMARY = 'executive-summary',
  SWOT_ANALYSIS = 'swot-analysis',
  BUSINESS_MODELS = 'business-models',
  VIABILITY_ANALYSIS = 'viability-analysis',
  MARKET_RESEARCH = 'market-research',
  PRODUCTS_SERVICES = 'products-services',
  SALES_MARKETING = 'sales-marketing',
  OPERATIONS = 'operations',
  FINANCIALS = 'financials',
  IMPLEMENTATION_PLAN = 'implementation-plan'
}

export type SectionStatus = 'not-started' | 'generating' | 'completed' | 'error';

export interface ValidationRule {
  id: string;
  type: 'required' | 'content' | 'format';
  description: string;
  status: 'passed' | 'failed' | 'warning';
  message?: string;
  feedback?: string;
}

export interface SectionPrompt {
  main: string;
  subsections?: Record<string, string>;
}

export interface SectionInstructions {
  title: string;
  requiredElements: string[];
  objectives: string[];
  contentGuidelines: string[];
}

export interface BusinessPlanSubsection {
  id: string;
  title: string;
  content: string;
  status: SectionStatus;
  pageNumber: number;
}

export interface BusinessPlanSection {
  id: SectionId;
  title: string;
  content: string;
  status: SectionStatus;
  validationRules?: ValidationRule[];
  pageNumber: number;
  subsections?: BusinessPlanSubsection[];
  error?: string;
}

export interface BusinessPlan {
  id: string;
  idea_id: string;
  user_id?: string;
  version: number;
  sections: Record<SectionId, BusinessPlanSection>;
  // Legacy fields - keeping for backward compatibility
  content?: {
    executive_summary?: string;
    market_analysis?: string;
    organization?: string;
    financial_plan?: string;
    marketing_strategy?: string;
  };
  executive_summary?: string;
  market_analysis?: string;
  organization?: string;
  financial_plan?: string;
  marketing_strategy?: string;
  created_at: string;
  updated_at: string;
  status?: 'generating' | 'completed' | 'draft';
  uiError?: string;
  title?: string;
  businessIdea?: BusinessIdea;
  additionalData?: BusinessPlanAdditionalData;
}

// Extended version of BusinessPlan with additional fields for UI and integration
export interface ExtendedBusinessPlan extends BusinessPlan {
  additionalData?: BusinessPlanAdditionalData;
  // Any fields that might be needed for the UI but not stored in Supabase
  businessIdea?: BusinessIdea;
}

export interface BusinessPlanFile {
  id: string;
  name: string;
  size: number;
  type: string;
  created_at: string;
  updated_at: string;
  path: string;
}

export interface BusinessPlanVersion {
  version: number;
  content: BusinessPlan['content'];
  created_at: string;
  created_by: string;
  comment?: string;
}

export interface GenerationContext {
  businessIdea: BusinessIdea;
  currentSection: SectionId;
  sections: Record<SectionId, BusinessPlanSection>;
  additionalData?: BusinessPlanAdditionalData;
}

// Metadata for each section of the business plan
export interface SectionMetadata {
  title: string;
  pageNumber: number;
  description?: string;
}

// Define the structure and order of the business plan sections
export const SECTION_METADATA: Record<SectionId, SectionMetadata> = {
  'cover-page': {
    title: 'Cover Page',
    pageNumber: 1,
    description: 'Title page with business name, contact information, and date'
  },
  'table-of-contents': {
    title: 'Table of Contents',
    pageNumber: 2,
    description: 'List of all sections with page numbers'
  },
  'overview': {
    title: 'Business Overview',
    pageNumber: 3,
    description: 'Brief introduction to the business concept'
  },
  'executive-summary': {
    title: 'Executive Summary',
    pageNumber: 4,
    description: 'Concise summary of the entire business plan'
  },
  'swot-analysis': {
    title: 'SWOT Analysis',
    pageNumber: 5,
    description: 'Analysis of Strengths, Weaknesses, Opportunities, and Threats'
  },
  'business-models': {
    title: 'Business Models',
    pageNumber: 6,
    description: 'Description of revenue streams and business model'
  },
  'viability-analysis': {
    title: 'Viability Analysis',
    pageNumber: 7,
    description: 'Assessment of the business idea\'s viability'
  },
  'market-research': {
    title: 'Market Research',
    pageNumber: 8,
    description: 'Analysis of target market, competitors, and industry trends'
  },
  'products-services': {
    title: 'Products & Services',
    pageNumber: 9,
    description: 'Detailed description of products or services offered'
  },
  'sales-marketing': {
    title: 'Sales & Marketing',
    pageNumber: 10,
    description: 'Marketing strategy and sales approach'
  },
  'operations': {
    title: 'Operations',
    pageNumber: 11,
    description: 'Day-to-day operations, facilities, and equipment'
  },
  'financials': {
    title: 'Financial Projections',
    pageNumber: 12,
    description: 'Financial forecasts, startup costs, and funding requirements'
  },
  'implementation-plan': {
    title: 'Implementation Plan',
    pageNumber: 13,
    description: 'Timeline and milestones for launching the business'
  }
};

// Section markers that Claude will use to separate sections
export const SECTION_MARKERS = {
  'cover-page': '# Cover Page',
  'table-of-contents': '# Table of Contents',
  'overview': '# Business Overview',
  'executive-summary': '# Executive Summary',
  'swot-analysis': '# SWOT Analysis',
  'business-models': '# Business Models',
  'viability-analysis': '# Viability Analysis',
  'market-research': '# Market Research',
  'products-services': '# Products and Services',
  'sales-marketing': '# Sales and Marketing',
  'operations': '# Operations',
  'financials': '# Financials',
  'implementation-plan': '# Implementation Plan'
};
